/**
 * Settings Service
 *
 * API integration for user settings including onboarding preferences.
 * Uses the existing /users/me/settings endpoints.
 */

import { api, APIError } from './api';

/**
 * Unverified users get 403s from /users/me/settings (the active-user gate).
 * That is an expected pre-verification state, not an error worth console
 * noise — onboarding falls back to its localStorage cache.
 */
const isExpectedAuthGate = (error: unknown): boolean =>
  error instanceof APIError && (error.status === 401 || error.status === 403);

// New simplified onboarding settings structure
export interface OnboardingSettings {
  enabled: boolean;
  dismissedTips: string[];
  // Legacy fields for backward compatibility during migration
  completed?: boolean;
  skipped?: boolean;
  currentStep?: string | null;
  completedSteps?: string[];
  disabledSteps?: string[];
}

export interface UserSettings {
  onboarding?: OnboardingSettings;
  // Future settings can be added here
}

class SettingsService {
  /**
   * Get all user settings
   */
  async getSettings(): Promise<UserSettings> {
    return api.get<UserSettings>('/users/me/settings');
  }

  /**
   * Update user settings (merges with existing)
   */
  async updateSettings(updates: Partial<UserSettings>): Promise<UserSettings> {
    return api.patch<UserSettings>('/users/me/settings', updates);
  }

  /**
   * Get onboarding settings specifically
   */
  async getOnboardingSettings(): Promise<OnboardingSettings | null> {
    try {
      const settings = await this.getSettings();
      return settings.onboarding || null;
    } catch (error) {
      if (!isExpectedAuthGate(error)) {
        console.error('Failed to get onboarding settings:', error);
      }
      return null;
    }
  }

  /**
   * Update onboarding settings specifically
   */
  async updateOnboardingSettings(updates: Partial<OnboardingSettings>): Promise<OnboardingSettings | null> {
    try {
      const currentSettings = await this.getSettings();
      const currentOnboarding = currentSettings.onboarding || {
        enabled: true,
        dismissedTips: [],
      };

      // Merge updates with current onboarding settings
      const mergedOnboarding = {
        ...currentOnboarding,
        ...updates,
      };

      const result = await this.updateSettings({ onboarding: mergedOnboarding });
      return result.onboarding || null;
    } catch (error) {
      if (!isExpectedAuthGate(error)) {
        console.error('Failed to update onboarding settings:', error);
      }
      return null;
    }
  }
}

export const settingsService = new SettingsService();
